import { IComponentOptions, IQService, IScope } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { IEnrichedTeam, ITeam, ITeamsStoreState, TeamsActions, TeamService } from "@gtmhub/teams";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { ITeamEventArgs, TeamEvents } from "@gtmhub/teams/events";
import { getToken } from "@gtmhub/auth";
import { INgRedux } from "@gtmhub/state-management";
import { IdMap } from "@gtmhub/util";
import { GtmhubController } from "@gtmhub/core";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IModalComponent } from "@gtmhub/core/routing";
import { StateService } from "@uirouter/angularjs";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { IAssignee } from "@gtmhub/assignees";
import { ViewHistoryService } from "@gtmhub/view-history";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";

interface ITeamProfileStateParamsService {
  teamId: string;
  focusSelectManagerFiled?: boolean;
}

interface ITeamProfileComponentRedux {
  teams: IdMap<ITeam>;
  teamsFetched: boolean;
  teamsError: IUIError;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ITeamProfileBindings extends IModalComponent {}

interface IOrganizationChartScope extends IScope, IExtendedRootScopeService {
  focusSelectManagerFiled: boolean;
}

const bindStateToCtrl = (state: ITeamsStoreState): ITeamProfileComponentRedux => {
  const { map: teams, isFetched: teamsFetched, error: teamsError } = state.teams;
  return {
    teams,
    teamsFetched,
    teamsError,
  };
};

export interface TeamProfileCtrl extends ITeamProfileBindings, ITeamProfileComponentRedux {}
export class TeamProfileCtrl extends GtmhubController {
  private teamEvents: TeamEvents;
  team: IEnrichedTeam;
  token: string;
  indicators: {
    loading: boolean;
  };

  // initial data for the respective user-selectors
  managerId: string[];
  parentId: string[];

  inlineEditingEnabled = false;
  private checkingEnabledFeatures: boolean;
  private showNewExperience: boolean;
  public favoritesManager: FavoritesManager;

  static $inject = [
    "$scope",
    "$ngRedux",
    "TeamsActions",
    "$stateParams",
    "TeamService",
    "UIErrorHandlingService",
    "$uibModal",
    "$state",
    "FeatureToggleV2Service",
    "ViewHistoryService",
    "FavoritesManagerFactory",
    "AccountService",
    "$q",
  ];

  constructor(
    private $scope: IOrganizationChartScope,
    $ngRedux: INgRedux,
    teamsActions: TeamsActions,
    private $stateParams: ITeamProfileStateParamsService,
    private teamService: TeamService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $uibModal: IModalService,
    private $state: StateService,
    private featureToggleService: FeatureToggleV2Service,
    private viewHistoryService: ViewHistoryService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private accountService: AccountService,
    private $q: IQService
  ) {
    super();
    this.$scope.focusSelectManagerFiled = this.$stateParams.focusSelectManagerFiled;
    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());

    this.teamEvents = new TeamEvents(this.$scope);
    this.featureToggleService.isFeatureAvailable(FeatureFlag.TeamProfileInlineEditing).then((enabled) => (this.inlineEditingEnabled = enabled));
    this.favoritesManager = this.favoritesManagerFactory(["user", "team"]);
  }

  $onInit(): void {
    this.indicators = {
      loading: true,
    };

    this.teamEvents.onTeamUpdated((args) => this.onTeamUpdated(args));

    this.token = getToken();

    this.checkingEnabledFeatures = true;
    this.showNewExperience = false;
    const promises = {
      newExperienceEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperience),
      newExperienceToggleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperienceToggle),
    };

    this.$q
      .all(promises)
      .then(({ newExperienceEnabled, newExperienceToggleEnabled }) => {
        const newExperienceAccountSettingEnabled = this.accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience")?.enabled;
        this.showNewExperience = newExperienceEnabled && newExperienceToggleEnabled && newExperienceAccountSettingEnabled;
        this.checkingEnabledFeatures = false;
        if (this.showNewExperience) {
          this.favoritesManager.initFavorites();
        }
      })
      .catch(() => (this.checkingEnabledFeatures = false));

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.teamsFetched,
        () => this.teamsError
      )
      .then(() => {
        this.teamService.getTeams({ filter: { _id: this.$stateParams.teamId }, fields: "id,parentId,badges" }).then((teamsCollection) => {
          // there always be only one element in the items
          const team = teamsCollection.items[0];
          this.team = { ...this.teams[this.$stateParams.teamId], parentId: team.parentId, badges: team.badges, type: "team" };
          this.indicators.loading = false;

          if (this.team.manager) {
            this.managerId = [this.team.manager];
          }

          if (this.team.parentId) {
            this.parentId = [this.team.parentId];
          }

          this.viewHistoryService.addItemToViewHistory(
            this.viewHistoryService.payloadBuilder.withTeam(team).forSource("navigation").generate(),
            navItemsFactory.buildTeamNavItem(this.team)
          );
        });
      })
      .catch((error) => {
        this.indicators.loading = false;
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  public navigateToProfile(assignee: IAssignee) {
    if (assignee) {
      const goToUrl = assignee.type === "user" ? ".employee" : "gtmhub.teams.team";
      const goToUrlParamProp = assignee.type === "user" ? "employeeId" : "teamId";

      this.$state.go(goToUrl, { [goToUrlParamProp]: assignee.id });
    }
  }

  public updateManagerId(id: string) {
    if (id !== this.team.manager) {
      this.managerId = id ? [id] : [];
      this.updateField("manager", id);
    }
  }

  public updateParentId(id: string) {
    if (id !== this.team.parentId) {
      this.parentId = id ? [id] : [];
      this.updateField("parentId", id);
    }
  }

  public updateTeamMembers(memberIds: string[]): void {
    const oldMemberIds = new Set(this.team.members);
    const valueChange = oldMemberIds.size !== memberIds.length || !memberIds.every((id) => oldMemberIds.has(id));

    if (valueChange) {
      this.updateField("members", memberIds);
    }
  }

  public updateField(field: keyof ITeam, value: string | string[]): void {
    const team: Partial<ITeam> = {
      id: this.team.id,
      [field]: value,
    };

    this.teamService.updateTeam(team).then(
      (updated) => {
        this.team = Object.assign(this.team, updated);
        this.teamEvents.broadcastTeamUpdated({ team: this.team });
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  public openAvatarSelector = (): void => {
    this.$state.go("._form.form", { teamId: this.team.id, "avatar-selector-only": true });
  };

  public confirmDeleteTeam(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../views/confirm-delete.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.confirm = () => {
            ctrl.deleteTeam();
            $uibModalInstance.close();
            ctrl.close();
          };

          $scope.close = () => {
            $uibModalInstance.close();
          };
        },
      ],
    });
  }

  private deleteTeam(): void {
    this.indicators.loading = true;
    this.teamService.deleteTeam(this.$stateParams.teamId).then(
      () => {
        this.indicators.loading = false;
        this.teamEvents.broadcastTeamDeleted({ teamId: this.team.id, parentId: this.team.parentId });
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private onTeamUpdated = (args: ITeamEventArgs): void => {
    this.team = { ...this.team, ...args.team, type: "team" };
  };

  public getNavItem = (): INavItem => {
    if (!this.team) return undefined;
    return this.favoritesManager.getFavoriteItem({ id: this.team.id }) || navItemsFactory.buildTeamNavItem(this.team);
  };

  public teamIsFavorite = (): boolean => {
    if (!this.team) return false;
    if (!this.isNewExperienceEnabled()) return false;
    return this.favoritesManager.entityIsFavorite({ id: this.team.id });
  };

  public isNewExperienceEnabled = (): boolean => {
    return !this.checkingEnabledFeatures && this.showNewExperience;
  };
}

export const TeamProfileComponent: IComponentOptions = {
  controller: TeamProfileCtrl,
  bindings: {
    dismiss: "&",
    close: "&",
  },
  templateUrl: require("./team-profile.component.jade"),
};
